import { ArrowsAltOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { Breadcrumb, Col, Row, DatePicker, message } from "antd";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useMutation } from "react-query";
import { Link } from "react-router-dom";
import "./billStyle.css";
import BillsList from "./BillsList";
import { approveBill, deleteBill, getBillById, getBills, getBillsResources, payBill } from "../../api/bills";
import AddButton from "../UI/AddButton/addbutton";
import ResetButton from "../UI/ResetButton/resetbutton";
import DeleteModal from "./deletemodal";
import { Search } from "../UI/Search/Search";
import { Bill, Pagination } from "./Types";

const Billings = () => {
  const navigate = useNavigate();
  const { t } = useTranslation("common");
  const { RangePicker } = DatePicker;

  const [selectedRecord, setSelectedRecord] = useState<Bill>()
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false)

  //data
  const [bills, setBills] = useState<Bill[]>();

  const [pagination, setPagination] = useState<Pagination>({
    current: 1,
    pageSize: 10,
    total: 0,
    limit: 10,
  });

  //search
  const [search, setSearch] = useState<string>("");

  //Date picker
  const [rangeDate, setRangeDate] = useState({ from: '', to: '' })

  //re-render datePicker and table on reset change
  const [tableKey, setTableKey] = useState<number>(0);

  const toggleDeleteModalOpen = () => {
    setDeleteModalOpen(!deleteModalOpen)
  }

  const { mutateAsync: deleteBillMutation, isLoading: deleteBillMutationLoading } = useMutation(
    "deleteBill",
    () => deleteBill(selectedRecord?.id),
    {
      onSuccess: ({ data }) => {
        toggleDeleteModalOpen()
        message.success(data.message)
      },
      onError: ({ data }) => {
        message.error(data ? data.message : 'Error Deleting Bill')
      }
    }
  );

  const { mutateAsync: payBillMutation, isLoading: payBillMutationLoading } = useMutation(
    "payBill",
    () => payBill(selectedRecord?.id),
    {
      onSuccess: ({ data }) => {
        setBills(bills?.map((item: Bill) => {
          if (item.id === data.id) {
            const { message, ...updatedItem } = data;
            return updatedItem;
          }
          return item;
        }));
        setSelectedRecord(data)
        message.success(data.message)
      },
      onError: ({ response }) => {
        const { data } = response
        message.error(data ? data.message : 'Error Paying Bill')
      },
    }
  );

  const { mutateAsync: approveBillMutation, isLoading: approveBillMutationLoading } = useMutation(
    "approveBill",
    () => approveBill(selectedRecord?.id),
    {
      onSuccess: ({ data }) => {
        setBills(bills?.map((item: Bill) => {
          if (item.id === data.id) {
            const { message, ...updatedItem } = data;
            return updatedItem;
          }
          return item;
        }));
        setSelectedRecord(data)
        message.success(data.message)
      },
      onError: ({ data }) => {
        message.error(data ? data.message : 'Error Approving Bill')
      }
    }
  );

  const { mutateAsync: getBillsMutation, isLoading: getBillsMutationLoading } = useMutation(
    "bills",
    ({ q, limit, offset, from, to }: { q?: string; limit?: number, offset?: number, from?: string, to?: string }) =>
      getBills({ q, limit, offset, from, to }),
    {
      onSuccess: ({ data }) => {
        setBills(data.data);
        // for searching
        if (data.total !== pagination.total) {
          setPagination(prevPagination => ({
            ...prevPagination,
            total: data.total,
            current: 1
          }));
        }
        setSelectedRecord(undefined)
      },
      onError: ({ data }) => {
        message.error(data ? data.message : "Error Getting Bills")
      }
    }
  );

  const { mutateAsync: getBillByIdMutation, isLoading: getBillByIdMutationLoading } = useMutation("user", getBillById);

  const { mutateAsync: getBillsResourcesMutation, isLoading: getBillsResourcesMutationLoading } = useMutation("billsResources", getBillsResources)

  const handleDeleteModalOk = async () => {
    await deleteBillMutation()
    await getBillsMutation({ q: search, limit: pagination.limit, offset: (pagination.current - 1) * pagination.pageSize, from: rangeDate.from, to: rangeDate.to });
  };

  const handleCreateEditBill = async (value: number | undefined) => {
    const responses = await Promise.all([
      getBillsResourcesMutation(),
      value ? getBillByIdMutation(value) : Promise.resolve(null)
    ])
  
    navigate(value ? `/billsedit/${value}` : `/billscreate`, {
      state: {
        paymentMethods: responses[0].data.paymentModes,
        suppliers: responses[0].data.suppliers,
        warehouses: responses[0].data.warehouses,
        currencies: responses[0].data.currencies,
        bill: value ? responses[1]?.data.bill : null,
      },
    });
  };

  const resetFilters = () => {
    if (!search && !rangeDate.from && !rangeDate.to) return

    setTableKey((tableKey) => tableKey + 1);
    getBillsMutation({ limit: pagination.limit, offset: (pagination.current - 1) * pagination.pageSize });
    setRangeDate({ from: "", to: "" })
    setSearch("");

    setPagination(prevPagination => ({
      ...prevPagination,
      current: 1
    }))
  };

  const handleBillAprove = async () => {
    try {
      await approveBillMutation();
    } catch (error) {
      console.error(error);
    }
  };

  const handleTableChange = (pagination: Pagination) => {
    setPagination(pagination);
    getBillsMutation({ q: search, limit: pagination.limit, offset: (pagination.current - 1) * pagination.pageSize, from: rangeDate.from, to: rangeDate.to })
  };

  const handleDateChange = (stringDates: any) => {
    setRangeDate({ from: stringDates[0], to: stringDates[1] })
    getBillsMutation({ q: search, limit: pagination.limit, offset: (pagination.current - 1) * pagination.pageSize, from: stringDates[0], to: stringDates[1] })
  }

  const isLoading = payBillMutationLoading || approveBillMutationLoading || getBillsMutationLoading || getBillByIdMutationLoading || getBillsResourcesMutationLoading
  const isSelected = !isLoading && selectedRecord

  // initial load
  useEffect(() => {
    getBillsMutation({ q: search, limit: pagination.limit, offset: (pagination.current - 1) * pagination.pageSize, from: rangeDate.from, to: rangeDate.to });
  }, [])

  return (
    <>
      <Row gutter={[10, 10]}>
        <Col offset={1} span={22}>
          <Breadcrumb
            items={[
              {
                title: <Link to="/main">{t("home")}</Link>,
              },
              {
                title: <>{t("purchases")}</>,
              },
              {
                title: (
                  <span className="breadcrumbcolor"> {t("bills-list")}</span>
                ),
              },
            ]}
          />
        </Col>
        <Col offset={1} span={22}>
          {/* <Title title={`${t("bills-list")}`} /> */}
        </Col>

        <Col offset={1} span={4}>
          <Search
            value={search}
            disabled={isLoading}
            onChange={(e: any) => setSearch(e.target.value)}
            onEnter={() => {
              getBillsMutation({ q: search, limit: pagination.limit, offset: (pagination.current - 1) * pagination.pageSize, from: rangeDate.from, to: rangeDate.to })
            }}
          />
        </Col>

        <Col span={7}>
          <RangePicker
            disabled={isLoading}
            size="large"
            key={tableKey}
            format={"MM/DD/YYYY"}
            placeholder={[t('start-date'), t('end-date')]}
            onChange={(dates, stringDates) => {
              handleDateChange(stringDates)
            }}
          />
        </Col>
        <Col
          offset={5}
          span={2}
          className="iconcolors"
        >
          <div onClick={() => { isSelected && navigate(`/itembill/${selectedRecord?.id}`, { state: { disabled: selectedRecord.isApproved, invoiceNo: selectedRecord.invoiceNo } }) }} className="viewicon" style={isSelected ?
            { color: '', cursor: 'pointer' } : { color: 'gray', cursor: 'not-allowed' }}>
            <ArrowsAltOutlined style={{
              fontSize: "22px",
            }} />
            <span style={isSelected ? { color: '', cursor: 'pointer' } : { color: 'gray', cursor: 'not-allowed' }}>{t("view")}</span>
          </div>

          <div onClick={() => {
            isSelected && !selectedRecord?.isApproved && handleCreateEditBill(selectedRecord?.id);
          }}
            className="viewicon"
            style={isSelected && !selectedRecord?.isApproved ? { color: "#1677FF", cursor: "pointer" } : { color: "#9E9E9E", cursor: "not-allowed" }}
          >
            <EditOutlined style={{ fontSize: "22px" }} />
            <span>{t("edit")}</span>
          </div>
          <div
            className="viewicon"
            style={isSelected && selectedRecord?.hasItems && !selectedRecord?.isApproved ? { color: "#290052", cursor: "pointer" } : { color: "#9E9E9E", cursor: "not-allowed" }}
            onClick={() => { isSelected && selectedRecord?.hasItems && !selectedRecord?.isApproved && handleBillAprove() }}
          >
            <CheckCircleOutlineIcon style={{ fontSize: "22px" }} />
            <span>{t("approve")}</span>
          </div>
          {/* <div
            className="viewicon"
            style={isSelected && selectedRecord?.isApproved && !selectedRecord?.isPaid ? { color: "#00A010", cursor: "pointer" } : { color: "#9E9E9E", cursor: "not-allowed" }}
            onClick={() => {
              isSelected && selectedRecord?.isApproved && !selectedRecord?.isPaid && payBillMutation();
            }}
          >
            <MonetizationOnOutlinedIcon style={{ fontSize: "22px" }} />
            <span>{t("pay")}</span>
          </div> */}
          {/* <div
            className="viewicon"
            style={
              isSelected && selectedRecord?.hasItems && selectedRecord?.isApproved
                ? {
                  color: "#FFB41F",
                  cursor: "pointer",
                }
                : { color: "#9E9E9E", cursor: "not-allowed" }
            }
            onClick={() => {
              isSelected && selectedRecord?.hasItems && selectedRecord?.isApproved && getBillByIdMutation(selectedRecord?.id).then((res) => {
                navigate(`/return/create`, {
                  state: res?.data,
                });
              });
            }}
          >
            <img src={isSelected && selectedRecord?.hasItems && selectedRecord?.isApproved ? image : img} alt="" className="image" />

            <span>{t("return")}</span>
          </div> */}
          <div
            onClick={() => isSelected && !selectedRecord?.isApproved && toggleDeleteModalOpen()}
            className="viewicon"
            style={
              isSelected && !selectedRecord?.isApproved
                ? {
                  color: "#F52C1D",
                  cursor: "pointer",
                }
                : {
                  color: "#9E9E9E",
                  cursor: 'not-allowed'
                }
            }
          >
            <DeleteOutlined style={{ fontSize: "22px" }} />
            <span>{t("delete")}</span>
          </div>
          {/* <div
            className="viewicon"
            style={
              !selectedRecord?.isApproved
                ? {
                  color: "#424242",
                }
                : { color: "#9E9E9E" }
            }
          >
            <PrinterOutlined style={{ fontSize: "22px" }} />
            <span>{t("print")}</span>
          </div> */}
        </Col>

        <Col
          offset={2}
          span={2}
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "20px",
          }}
        >
          <ResetButton
            disabled={isLoading}
            onClick={() => {
              resetFilters();
            }}
          />
          <Col style={{ display: "flex", justifyContent: "flex-end" }}>
            <AddButton
              disabled={isLoading}
              onClick={() => {
                handleCreateEditBill(undefined);
              }}
            />
          </Col>
        </Col>

        <Col offset={1} span={22}>
          <BillsList
            tableKey={tableKey}
            selectedRecord={selectedRecord}
            setSelectedRecord={setSelectedRecord}
            handleTableChange={handleTableChange}
            pagination={pagination}
            data={bills}
            isLoading={isLoading}
          />
        </Col>
      </Row>
      <DeleteModal open={deleteModalOpen} setOpen={setDeleteModalOpen} name={""} onOk={handleDeleteModalOk} isLoading={deleteBillMutationLoading} />
    </>
  );
};

export default Billings;
